import { createContext, useContext, useMemo } from 'react'
import { useExperimentContext } from '@/experimentation'
import { useUser } from '@/services/UserService'

interface MobileNavContextProviderValues {
  isMobileNavHidden: boolean
  isDefaultMobileNavLayout: boolean
}

const MobileNavContextProvider = createContext<MobileNavContextProviderValues>({
  isMobileNavHidden: true,
  isDefaultMobileNavLayout: true,
})

interface MobileNavHiddenStateProviderProps {
  children: React.ReactNode
  useDefaultNav: boolean
  shouldRemoveNavOnMobile: boolean
}

export const MobileNavHiddenStateProvider: React.FC<MobileNavHiddenStateProviderProps> = ({
  children,
  useDefaultNav,
  shouldRemoveNavOnMobile,
}) => {
  const { isLoggedIn, loading: loadingUser } = useUser()
  const { getFeatureValue } = useExperimentContext()

  const isMobileNavHidden = useMemo(() => {
    if (loadingUser) return true

    if (useDefaultNav) {
      if (isLoggedIn) return true
      return getFeatureValue('theatrical-hide-nav-logged-out-users', false)
    }

    return shouldRemoveNavOnMobile
  }, [useDefaultNav, isLoggedIn, loadingUser, shouldRemoveNavOnMobile, getFeatureValue])

  // This controls the style of the nav, not the visibility
  const isDefaultMobileNavLayout = useMemo(() => {
    if (loadingUser) return true
    return useDefaultNav ? !isLoggedIn : true
  }, [useDefaultNav, isLoggedIn, loadingUser])

  return (
    <MobileNavContextProvider.Provider value={{ isMobileNavHidden, isDefaultMobileNavLayout }}>
      {children}
    </MobileNavContextProvider.Provider>
  )
}

export const useMobileNavHiddenState = () => {
  const context = useContext(MobileNavContextProvider)
  if (context === undefined) {
    throw new Error('useMobileNavHiddenState must be used within a MobileNavHiddenStateProvider')
  }
  return context
}
